// Candidate AV1 codec strings ordered by likelihood and capability.
const AV1_CODEC_CANDIDATES = [
  'av01.0.08M.10', // Main, 10-bit, L4.0 – common for 4K/HDR capable devices
  'av01.0.05M.10', // Main, 10-bit, L3.x – mid-tier
  'av01.0.05M.08', // Main, 8-bit, L3.x – baseline
] as const

const CONTAINERS = [
  'video/mp4', // Primary for Safari/Apple platforms and many others
  'video/webm', // Some Chromium browsers support AV1 in WebM
]

function getAV1ContentTypes(): string[] {
  const res: string[] = []
  for (const container of CONTAINERS) {
    for (const codec of AV1_CODEC_CANDIDATES) {
      res.push(`${container}; codecs="${codec}"`)
    }
  }
  return res
}

function hasDOM(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

let cachedAV1Support: boolean | null = null

export function isAV1Supported(): boolean {
  // Robust AV1 support detection covering MP4 and WebM containers, file and MSE.
  if (!hasDOM()) return false
  if (cachedAV1Support !== null) return cachedAV1Support

  const videoEl = document.createElement('video')
  const types = getAV1ContentTypes()

  // Check file playback support via canPlayType
  for (const t of types) {
    const can = videoEl.canPlayType(t)
    if (can === 'probably' || can === 'maybe') {
      cachedAV1Support = true
      return true
    }
  }

  // Check Media Source Extensions (streaming) support
  if (typeof MediaSource !== 'undefined' && typeof MediaSource.isTypeSupported === 'function') {
    for (const t of types) {
      if (MediaSource.isTypeSupported(t)) {
        cachedAV1Support = true
        return true
      }
    }
  }

  cachedAV1Support = false
  return false
}

/**
 * Checks if AV1 playback has hardware acceleration support.
 * Only returns true if the device can decode AV1 in a power-efficient way (hardware).
 * Software-only AV1 decoding is rejected to avoid battery drain and poor performance.
 */
// Minimal types for MediaCapabilities API
type MediaDecodingType = 'file' | 'media-source'
interface MediaDecodingConfiguration {
  type: MediaDecodingType
  video?: {
    contentType: string
    width: number
    height: number
    bitrate: number
    framerate: number
  }
}
interface MediaCapabilitiesDecodingInfo {
  supported: boolean
  smooth: boolean
  powerEfficient: boolean
}

export async function isAV1PlaybackSmooth(): Promise<boolean> {
  if (!hasDOM()) return false

  // MediaCapabilities API is required to check for hardware acceleration
  const hasMediaCapabilities = typeof navigator !== 'undefined' && 'mediaCapabilities' in navigator
  if (!hasMediaCapabilities) {
    // Can't determine hardware support without MediaCapabilities API - default to VP9/H.264
    return false
  }

  const types = getAV1ContentTypes()

  // Build a few typical resolution/bitrate targets to probe. Keep it small to be fast.
  const targets = [
    { width: 1280, height: 720, framerate: 30, bitrate: 2500_000 }, // 720p30 ~2.5Mbps
    { width: 1920, height: 1080, framerate: 30, bitrate: 5000_000 }, // 1080p30 ~5Mbps
  ]

  // Try file decoding first (progressive playback), then MSE (streaming) if needed.
  for (const contentType of types) {
    for (const t of targets) {
      const fileConfig: MediaDecodingConfiguration = {
        type: 'file',
        video: { contentType, width: t.width, height: t.height, framerate: t.framerate, bitrate: t.bitrate },
      }
      try {
        // @ts-ignore – MediaCapabilities exists in modern browsers
        const resFile: MediaCapabilitiesDecodingInfo = await navigator.mediaCapabilities.decodingInfo(fileConfig)
        if (resFile.supported && resFile.smooth && resFile.powerEfficient) return true
      } catch {
        // ignore and continue trying other combos
      }
    }
  }

  // As a second chance, try media-source (MSE) profile which some browsers optimize differently.
  for (const contentType of types) {
    for (const t of targets) {
      const mseConfig: MediaDecodingConfiguration = {
        type: 'media-source',
        video: { contentType, width: t.width, height: t.height, framerate: t.framerate, bitrate: t.bitrate },
      }
      try {
        // @ts-ignore – MediaCapabilities exists in modern browsers
        const resMse: MediaCapabilitiesDecodingInfo = await navigator.mediaCapabilities.decodingInfo(mseConfig)
        if (resMse.supported && resMse.smooth && resMse.powerEfficient) return true
      } catch {
        // ignore and continue trying other combos
      }
    }
  }

  // No hardware-accelerated AV1 support found
  // Don't fall back to software decoding - it's not power efficient
  return false
}

/**
 * Determines if the current device is an iOS device
 */
export function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Macintosh') && 'ontouchend' in document)
  )
}

/**
 * Video data structure for selecting the appropriate video path
 */
interface VideoData {
  path: string
}

interface VideoChapterForSelection {
  standard: VideoData
  mobile: VideoData
  avStandard?: VideoData
  avMobile?: VideoData
  vp9Standard?: VideoData
  vp9Mobile?: VideoData
}

interface VideoSelectionPrefs {
  isMobile: boolean
  useAV1: boolean
}

/**
 * Selects the appropriate video path based on device type and codec support.
 * This is the single source of truth for video path selection logic.
 *
 * Priority:
 * 1. iOS devices always use H.264 (standard/mobile)
 * 2. If useAV1 is true, prefer AV1 variants with H.264 fallback
 * 3. Otherwise prefer VP9 variants with H.264 fallback
 *
 * @param chapter - The video chapter object containing all video variants
 * @param prefs - User preferences including isMobile and useAV1 flags
 * @returns The appropriate video path string
 */
export function getVideoPathForChapter(chapter: VideoChapterForSelection, prefs: VideoSelectionPrefs): string {
  const isIOS = isIOSDevice()

  if (prefs.isMobile) {
    if (isIOS) {
      return chapter.mobile.path
    }
    if (prefs.useAV1) {
      return chapter.avMobile?.path ?? chapter.mobile.path
    }
    return chapter.vp9Mobile?.path ?? chapter.mobile.path
  }

  // Desktop
  if (isIOS) {
    return chapter.standard.path
  }
  if (prefs.useAV1) {
    return chapter.avStandard?.path ?? chapter.standard.path
  }
  return chapter.vp9Standard?.path ?? chapter.standard.path
}
